import React, { useState, useRef } from "react"
import { ActionIcon, Button, Container, Flex, Image, List, Space, Text, useMantineTheme } from "@mantine/core"
import { UnstyledButton, Icon } from "@/components"

import { useStyles } from "@/components/TopNavigation/components/SearchPopdown/SearchPopdownAi.styles"
import urls from "app/static/frontend/imports/desktopPaths"
import SegueLink from "app/static/frontend/middleware/SegueLink"

import { getAiSearchExamples } from "app/static/frontend/search-new/helperFunctions"
import { AiSearchLoadingGif } from "@/components/TopNavigation/components/SearchPopdown/AiSearchLoadingGif"
import { AI_SEARCH_ERROR_IMG_URL, AI_SEARCH_NOT_FOUND_IMG_URL, MAX_AI_SEARCH_CHARACTERS } from "shared/search/constants"

import { SearchPopdownAiProps } from "@/components/TopNavigation/types"

export const SearchPopdownAi = (props: SearchPopdownAiProps) => {
    const [isLearnMoreExpanded, setIsLearnMoreExpanded] = useState(false)
    const { classes } = useStyles({ isLearnMoreExpanded })
    const theme = useMantineTheme()
    const {
        aiSearchCancelOnClick,
        aiSearchExampleOnClick,
        aiSearchExamples,
        aiSearchStatus,
        aiSearchToggle,
        onCloseSearchPopdown,
        searchPopdownRef,
    }: any = props

    const toggleIsLearnMoreExpanded = () => setIsLearnMoreExpanded((previousExpanded) => !previousExpanded)

    const aiSearchExamplesRef = useRef(getAiSearchExamples().examples)

    // Adapted from QuorumDesign/src/Components/Layouts/PopdownOverlay/index.js for use with <TopNavigation>
    const renderAiSearchExamples = () => (
        <div>
            {aiSearchExamplesRef.current.map((example) => {
                return (
                    <UnstyledButton
                        data-plum-id="ai-example-search"
                        onClick={() => {
                            aiSearchExampleOnClick(example.initialFilter, example.qdt)
                        }}
                        className={classes.aiSearchSegue}
                        key={`ai-search-example-${example.initialFilter.ai_search}`}
                    >
                        <div className={classes.aiExampleItem}>
                            <div className={classes.aiExampleText}>
                                <span className={classes.aiExampleBulletPoint}>
                                    <Icon icon="sparkle" iconFamily="q" color={theme.colors.violet[3]} size="xs" />
                                </span>
                                <span>{example.initialFilter.ai_search}</span>
                            </div>
                            <div className={classes.aiExampleButton}>
                                <Icon icon="sparkles" iconFamily="fas" />
                                &nbsp;Search
                            </div>
                        </div>
                    </UnstyledButton>
                )
            })}
        </div>
    )

    const renderAiSearchMain = () => (
        <>
            <div className={classes.aiPaddedText}>
                <Text className={classes.aiTitle}>
                    Searching with AI-powered engine
                    {/* @ts-expect-error */}
                    <SegueLink to={urls.searchbar} onClick={onCloseSearchPopdown}>
                        <ActionIcon variant="subtle">
                            <Icon icon="cog" color={theme.colors.gray[7]} />
                        </ActionIcon>
                    </SegueLink>
                </Text>
                <Button
                    size="xs"
                    variant="transparent"
                    onClick={toggleIsLearnMoreExpanded}
                    className={classes.aiLearnMoreButton}
                >
                    Learn more
                    <Icon icon={isLearnMoreExpanded ? "angle-up" : "angle-down"} />
                </Button>
                {isLearnMoreExpanded && (
                    <div className={classes.aiExpandedText}>
                        AI search is now available for select datasets! You can create a search as quickly as typing a
                        few words - no need to select multiple filters or insert boolean operators.
                        <Space h="md" />
                        Get started by typing in a search or selecting an example below.
                    </div>
                )}
                {renderAiSearchExamples()}
            </div>
            <div className={classes.aiFooter}>
                <div>
                    <Icon icon="info-circle" iconFamily="fas" color={theme.colors.violet[5]} />
                    <span>
                        Available for&nbsp;
                        <span className={classes.aiFooterDatasets}>{aiSearchExamples.datasets.join(", ")}</span>
                    </span>
                </div>
                <Button className={classes.aiStandardSearchButton} onClick={aiSearchToggle} variant="subtle">
                    Go to regular search
                </Button>
            </div>
        </>
    )

    const renderLoadingState = () => (
        <Flex gap="lg" justify="center" align="center" direction="column" className={classes.aiLoading}>
            <AiSearchLoadingGif />
            Generating results...
        </Flex>
    )

    const getAiSearchAlertObj = (status) => {
        const obj = {
            imageUrl: null,
            text: null,
            buttons: null,
        }

        if (status === "error") {
            obj.imageUrl = AI_SEARCH_ERROR_IMG_URL
            obj.text = (
                <div>
                    Your search has exceeded the {MAX_AI_SEARCH_CHARACTERS} character limit for our AI search system.
                    <Space h="md" />
                    Please rephrase your search or try regular search.
                </div>
            )
            obj.buttons = (
                <Flex className={classes.aiAlertStateButtonContainer}>
                    <Button className={classes.aiAlertStateButtons} onClick={aiSearchToggle} variant="outline">
                        <Icon icon="search" />
                        &nbsp;Go to regular search
                    </Button>
                    <Button
                        className={classes.aiAlertStateCancelButtons}
                        onClick={aiSearchCancelOnClick}
                        variant="subtle"
                    >
                        Cancel
                    </Button>
                </Flex>
            )
        }

        if (status === "not found") {
            obj.imageUrl = AI_SEARCH_NOT_FOUND_IMG_URL
            obj.text = (
                <List spacing="md">
                    Our AI Search could not understand what you were trying to find. This could be because:
                    <Space h="md" />
                    <List.Item>
                        You were trying to search a dataset or filter that is not supported in our AI search yet
                    </List.Item>
                    <List.Item>The search text was not specific enough</List.Item>
                    <List.Item>Something else</List.Item>
                </List>
            )
            obj.buttons = (
                <Flex>
                    <Button className={classes.aiAlertStateButtons} onClick={aiSearchToggle} variant="outline">
                        <Icon icon="search" />
                        &nbsp;Go to regular search
                    </Button>
                    <div>
                        <Button variant="subtle" className={classes.aiAlertStateButtons}>
                            <Icon icon="thumbs-up" />
                        </Button>
                        <Button variant="subtle" className={classes.aiAlertStateButtons}>
                            <Icon icon="thumbs-down" />
                        </Button>
                    </div>
                </Flex>
            )
        }

        return obj
    }

    const renderAlertState = (aiSearchStatus) => {
        const alertObj = getAiSearchAlertObj(aiSearchStatus)

        return (
            <Flex gap="lg" justify="center" align="center" className={classes.aiAlertStateContainer}>
                <Image width={148} height={148} fit="contain" src={alertObj.imageUrl} />
                <Flex direction="column" gap="lg">
                    <Text className={classes.aiAlertStateTitle}>Uh-Oh!</Text>
                    <span className={classes.aiAlertObj}>{alertObj.text}</span>
                    {alertObj.buttons}
                </Flex>
            </Flex>
        )
    }

    let aiSearchPopdownContent

    switch (aiSearchStatus) {
        case "loading":
            aiSearchPopdownContent = renderLoadingState()
            break
        case "not found":
        case "error":
            aiSearchPopdownContent = renderAlertState(aiSearchStatus)
            break
        case "main":
        default:
            aiSearchPopdownContent = renderAiSearchMain()
            break
    }

    return (
        <Container className={classes.aiPopdown}>
            <div className={classes.aiContainer} ref={searchPopdownRef}>
                {aiSearchPopdownContent}
            </div>
        </Container>
    )
}
